import React, { useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import picture1 from "../Picture1.png";
import picture2 from "../Picture2.png";
import picture3 from "../Picture3.png";
import picture4 from "../Picture4.png";


const helpTabs = [
  {
    label: "SUBMIT A PROTEIN",
    title: "Submit A Protein",
    description: "Start your prediction by providing the protein structure you want PredPPIGReMLIN to analyze.",
    imageTitle: "Submission Preview",
    imageCaption: "Illustrative preview area for the protein submission workflow.",
    imageSrc: picture1,
    steps: [
        "Use this field to upload protein structures in PDB format.",
        "You can also submit structures from RCSB PDB directly by typing protein identifier.",
        "Click Run Prediction." 
    ]
  },
  {
    label: "LINK TO RESULT",
    title: "Link to Result",
    description: "Use the dedicated link to result to access the result after the process is completed.",
    imageTitle: "Result to Result Preview",
    imageCaption: "Illustrative preview area for result filtering, sorting, and chain review.",
    imageSrc: picture2,
    steps: [
      "A job ID and dedicated result link will display immediately. We recommend you copy and save link somewhere. The link is the only way to access your results, if you lose it, you will no longer be able to access the results."
    ]
  },
  {
    label: "RESULT PAGE",
    title: "Result Page",
    description: "Use the result page to review the prediction summary and explore the list of graph pattern matches.",
    imageTitle: "Result Page Preview",
    imageCaption: "Illustrative preview area for result filtering, sorting, and chain review.",
    imageSrc: picture3,
    steps: [
      "Filters the search results based on the selected target protein chains (e.g., Chain A, B) from Graph Query. Selecting a Target Chain will displays the corresponding Ligand Chain from the Graph Pattern Matches and enable the VISUALIZE button.",
      "Filters results according to the number of nodes (atoms/residues/features) in the Query Graph. This allows users to restrict results to a specific graph size for more consistent structural comparisons.",
      "Filters results according to the number of nodes (atoms/residues/features) in the Graph Patterns. This allows users to restrict results to a specific graph size for more consistent structural comparisons.",
      "Filters results by the number of graph pattern matches identified between the Query Graph and each Graph Corpus. Higher values indicate more matching subgraph patterns.",
      "Sets the minimum Pick Score threshold for displayed results. Only matches with a score equal to or greater than the specified value is shown, helping remove low-confidence matches.",
      "Specifies the attribute used to sort the results (e.g. Pick Score, Graph Query Size, or Graph Pattern Matches). The default shown is No Sorting.",
      "Determines the sorting order of the selected attribute. Asc sorts from lowest to highest, while Desc sorts from highest to lowest.",
      "Exports the filtered search results for offline analysis. Two formats are available: Download JSON for structured machine-readable data and Download CSV for spreadsheet analysis.",
      "Opens an interactive visualization of the selected graph match, allowing users to inspect the structural alignment, matched chains, ligand interactions, and graph correspondence for a specific result."
    ]
  },
  {
    label: "RESULT DETAIL",
    title: "Result Detail",
    description: "Open a detailed match view to inspect the selected target and ligand graph relationship more closely.",
    imageTitle: "Result Detail Preview",
    imageCaption: "Illustrative preview area for the detailed graph comparison experience.",
    imageSrc: picture4,
    steps: [
      "Displays the Query Graph used in the search. Each node represents a graph vertex (e.g., atom, residue, or structural feature), while edges represent their relationships. Nodes highlighted with red outlines indicate vertices participating in the target chain.",
      "Displays the graph representation of the Graph Pattern Match from the retrieved Corpus Graph. Red-circled nodes identify vertices that correspond to the mapped nodes in the target chain, allowing users to visually inspect the matched vertices.",
      "Presents the overall confidence or Pick Score of the graph match as a percentage (71% in this example). A higher percentage indicates a stronger structural correspondence between the target chain and the predicted ligand chain in the Graph Pattern Match.",
      "Candidate chain(s) predicted to correspond to the target chain."
    ]
  }
];

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box
      component="section"
      className="services"
      sx={{
        minHeight: "92vh",
        textAlign: "center",
        py: 6,
        px: { xs: 2, md: 6 }
      }}
    >
      <Box
        sx={{
          maxWidth: 1520,
          mx: "auto",
          background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
          border: "1px solid #d9e7f5",
          borderRadius: 4,
          boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 5 },
            background: "linear-gradient(135deg, #0f4c81 0%, #2a7ab9 100%)",
            color: "#fff"
          }}
        >
          <Typography variant="overline" sx={{ letterSpacing: "0.16em", fontWeight: 700 }}>
            Help Guide
          </Typography>
          <Typography variant="h3" sx={{ mt: 1, fontWeight: 800, fontSize: { xs: "2rem", md: "2.6rem" } }}>
            How to use PredPPIGReMLIN
          </Typography>
          <Typography sx={{ mt: 2, maxWidth: 760, mx: "auto", fontSize: "1.05rem", opacity: 0.95 }}>
            Follow the workflow below to submit a protein, review prediction results, and inspect detailed graph pattern matches.
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, backgroundColor: "#eef5fb" }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { backgroundColor: "#0f4c81", height: 4 } }}
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: 1,
                flexWrap: { xs: "nowrap", md: "wrap" }
              },
              "& .MuiTab-root": {
                minHeight: 54,
                borderRadius: "14px 14px 0 0",
                backgroundColor: "#dfeaf5",
                color: "#27445d",
                fontWeight: 700
              },
              "& .Mui-selected": {
                backgroundColor: "#ffffff",
                color: "#0f4c81"
              }
            }}
          >
            {helpTabs.map((tab, tabIndex) => (
              <Tab
                key={tab.label}
                id={`services-tab-${tabIndex}`}
                aria-controls={`services-panel-${tabIndex}`}
                label={tab.label}
              />
            ))}
          </Tabs>
        </Box>

        {helpTabs.map((tab, tabIndex) => (
          <Box
            key={tab.label}
            role="tabpanel"
            id={`services-panel-${tabIndex}`}
            hidden={activeTab !== tabIndex}
            aria-labelledby={`services-tab-${tabIndex}`}
            sx={{
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 5 },
              textAlign: "left",
              backgroundColor: "#fff",
              display: activeTab === tabIndex ? "block" : "none"
            }}
          >
            <Typography variant="h4" sx={{ color: "#16324a", fontWeight: 800 }}>
              {tab.title}
            </Typography>
            <Typography sx={{ mt: 1.5, color: "#4b5563", fontSize: "1rem", lineHeight: 1.7 }}>
              {tab.description}
            </Typography>

            <Box
              sx={{
                mt: 4,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2
              }}
            >
              {tab.steps.map((step, index) => (
                <Box
                  key={`${tab.label}-${index}`}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid #dbe5ef",
                    backgroundColor: "#f9fbfd",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)"
                  }}
                >
                  {/*
                    Keep step numbers continuous across tabs so the guide reads as one workflow.
                  */}
                  <Typography sx={{ color: "#0f4c81", fontWeight: 800, fontSize: "0.9rem", letterSpacing: "0.08em" }}>
                    STEP {helpTabs.slice(0, tabIndex).reduce((total, item) => total + item.steps.length, 0) + index + 1}
                  </Typography>
                  <Typography sx={{ mt: 1.5, color: "#1f2937", lineHeight: 1.75 }}>
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                mt: 5,
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: "1px solid #dbe5ef",
                background: "linear-gradient(180deg, #f3f8fc 0%, #ffffff 100%)"
              }}
            >
              <Typography variant="h5" sx={{ color: "#16324a", fontWeight: 800 }}>
                {tab.imageTitle}
              </Typography>
              <Typography sx={{ mt: 1, color: "#5b6877", lineHeight: 1.7 }}>
                {tab.imageCaption}
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  minHeight: 280,
                  borderRadius: 3,
                  border: "1px dashed #8fb2d1",
                  background: "radial-gradient(circle at top, rgba(42, 122, 185, 0.18), rgba(255, 255, 255, 0.95) 60%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  textAlign: "center",
                  px: 3
                }}
              >
                <Box
                  component="img"
                  src={tab.imageSrc}
                  alt={`${tab.title} illustration`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: 1050,
                    mx: "auto",
                    bgcolor: "white",
                    borderRadius: 2,
                    p: 1,
                    gap: 1
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Services;
